import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MessageCircle, ArrowLeft, Trash2, Pencil, Lock, Gift } from "lucide-react";
import { differenceInDays, format, parseISO, setYear } from "date-fns";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import MessageGenerator from "@/components/MessageGenerator";
import AboutScreen from "@/components/AboutScreen";
import { OCCASION_TYPES, type OccasionType } from "@/lib/occasion-types";
import { PaywallModal } from "@/components/PaywallModal";
import GiftBrowser from "@/components/GiftBrowser";

type Contact = Tables<"contacts">;


function getDaysUntilBirthday(birthdayStr: string): number {
  const today = new Date();
  const birthday = parseISO(birthdayStr);
  let next = setYear(birthday, today.getFullYear());
  today.setHours(0, 0, 0, 0);
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(birthday, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

interface ContactDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  occasions: OccasionData[];
  onEdit: (contact: Contact) => void;
  onDeleted: () => void;
}

interface OccasionData {
  id: string;
  occasion_type: OccasionType;
  label: string | null;
  date: string;
  is_recurring: boolean;
  converted_to_contact: boolean;
}

function hasEnrichmentData(contact: Contact): boolean {
  const c = contact as any;
  return !!(
    c.personality ||
    (c.loves && c.loves.length > 0) ||
    (c.dislikes && c.dislikes.length > 0) ||
    c.gift_ideas ||
    c.shared_memories ||
    c.notes
  );
}

const ContactDetail = ({ open, onOpenChange, contact, occasions, onEdit, onDeleted }: ContactDetailProps) => {
  const [showMessageGen, setShowMessageGen] = useState(false);
  const [messageOccasion, setMessageOccasion] = useState<{ type: OccasionType; label?: string; id?: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [localContact, setLocalContact] = useState<Contact | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [giftBrowserOpen, setGiftBrowserOpen] = useState(false);


  const handleAboutSaved = useCallback(async () => {
    // Re-fetch the contact to get updated enrichment data
    if (!contact) return;
    const { data } = await supabase.from("contacts").select("*").eq("id", contact.id).single();
    if (data) setLocalContact(data);
    onDeleted(); // Also refresh parent list
  }, [contact, onDeleted]);

  const displayContact = localContact && contact && localContact.id === contact.id ? localContact : contact;
  const isLocked = !!(displayContact as any)?.locked;

  if (!displayContact) return null;

  const c = displayContact as any;

  const handleDelete = async () => {
    if (!confirm(`Remove ${displayContact.full_name} from your circle?`)) return;
    setDeleting(true);
    const { error } = await supabase.from("contacts").delete().eq("id", displayContact.id);
    setDeleting(false);
    if (error) {
      toast.error("That didn't work — give it another go.");
    } else {
      toast.success(`${displayContact.full_name} removed from your circle.`);
      onOpenChange(false);
      onDeleted();
    }
  };

  const handleWriteMessage = (occasionType?: OccasionType, occasionLabel?: string, occasionId?: string) => {
    if (isLocked) {
      setPaywallOpen(true);
      return;
    }
    setMessageOccasion(occasionType ? { type: occasionType, label: occasionLabel, id: occasionId } : null);
    setShowMessageGen(true);
  };

  const handleEdit = () => {
    if (isLocked) {
      setPaywallOpen(true);
      return;
    }
    onOpenChange(false);
    setTimeout(() => onEdit(displayContact), 300);
  };

  const handleDismissNudge = async () => {
    await supabase
      .from("contacts")
      .update({ about_nudge_dismissed: true } as any)
      .eq("id", contact.id);
  };

  const firstName = displayContact.full_name.split(" ")[0];
  const enriched = hasEnrichmentData(displayContact);
  const nudgeDismissed = c.about_nudge_dismissed === true;

  if (showMessageGen) {
    return (
      <>
        <Sheet open={open} onOpenChange={(v) => { if (!v) { setShowMessageGen(false); setMessageOccasion(null); } onOpenChange(v); }}>
          <SheetContent className="overflow-y-auto bg-background sm:max-w-md">
            <div className="mb-4">
              <button
                onClick={() => { setShowMessageGen(false); setMessageOccasion(null); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> Back to {displayContact.full_name}
              </button>
            </div>
            <MessageGenerator
              contact={displayContact}
              occasionType={messageOccasion?.type}
              occasionLabel={messageOccasion?.label}
              occasionId={messageOccasion?.id}
              onOpenAbout={() => setAboutOpen(true)}
            />
          </SheetContent>
        </Sheet>
        <AboutScreen
          open={aboutOpen}
          onOpenChange={setAboutOpen}
          contact={displayContact}
          onSaved={handleAboutSaved}
        />
      </>
    );
  }

  // Build unified important dates list
  const allDates: Array<{
    key: string;
    occasionType: OccasionType;
    label: string | null;
    date: string;
    isRecurring: boolean;
    sortDays: number;
  }> = [];

  const birthdayDays = getDaysUntilBirthday(displayContact.birthday);
  allDates.push({
    key: "birthday",
    occasionType: "birthday",
    label: null,
    date: displayContact.birthday,
    isRecurring: true,
    sortDays: birthdayDays,
  });

  for (const occ of occasions) {
    if (occ.occasion_type === "birthday") continue;
    const days = getDaysUntilOccasionSafe(occ.date, occ.is_recurring);
    allDates.push({
      key: occ.id,
      occasionType: occ.occasion_type,
      label: occ.label,
      date: occ.date,
      isRecurring: occ.is_recurring,
      sortDays: days ?? 9999,
    });
  }

  allDates.sort((a, b) => a.sortDays - b.sortDays);

  const heroEntry = allDates[0];
  const heroDays = heroEntry.sortDays;
  const heroEmoji = OCCASION_TYPES[heroEntry.occasionType].emoji;


  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="overflow-y-auto bg-background p-0 sm:max-w-md">
          <SheetHeader className="sr-only">
            <SheetTitle>{displayContact.full_name}</SheetTitle>
          </SheetHeader>

          {/* Editorial cream hero */}
          <div className="relative bg-accent/10 px-6 pt-10 pb-8 text-center">
            <p className="text-eyebrow text-accent mb-3">
              {displayContact.relationship}
            </p>
            <h2 className="text-page-title text-foreground mb-1">
              {displayContact.full_name}
            </h2>
            <div className="mt-1 mx-auto h-0.5 w-10 rounded-full bg-accent" />

            <div className="mt-7 flex flex-col items-center">
              <span className="font-serif text-6xl font-semibold leading-none text-foreground">
                {heroDays}
              </span>
              <span className="mt-2 text-body text-muted-foreground">
                {heroDays === 0
                  ? `It's their ${OCCASION_TYPES[heroEntry.occasionType].label.toLowerCase()} today ${heroEmoji}`
                  : heroDays === 1
                    ? `day until ${OCCASION_TYPES[heroEntry.occasionType].label.toLowerCase()}`
                    : `days until ${OCCASION_TYPES[heroEntry.occasionType].label.toLowerCase()}`}
              </span>
            </div>
          </div>

          <div className="px-6 py-6">
            {/* Locked contact banner */}
            {isLocked && (
              <div className="mb-6 rounded-2xl border-l-[3px] border-l-accent bg-accent/5 p-4">
                <div className="flex items-start gap-2">
                  <Lock className="mt-0.5 shrink-0 text-accent" size={14} />
                  <div>
                    <p className="text-body text-foreground">
                      This contact is locked. Upgrade to Dearestly Plus to edit, add occasions, or write messages.
                    </p>
                    <button
                      onClick={() => setPaywallOpen(true)}
                      className="mt-2 text-sm font-medium text-accent hover:underline"
                    >
                      Upgrade to Plus →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Primary actions */}
            <div className="space-y-3">
              <Button
                className="w-full"
                size="lg"
                onClick={() => handleWriteMessage(heroEntry.occasionType, heroEntry.label ?? undefined, heroEntry.key !== "birthday" ? heroEntry.key : undefined)}
              >
                <MessageCircle className="mr-2 h-4 w-4 shrink-0" />
                Write a message →
              </Button>

              <Button
                variant="outline"
                className="w-full"
                size="lg"
                onClick={() => {
                  if (isLocked) {
                    setPaywallOpen(true);
                    return;
                  }
                  setGiftBrowserOpen(true);
                }}
              >
                {isLocked && <Lock className="mr-2 h-3.5 w-3.5" />}
                <Gift className="mr-2 h-4 w-4" />
                Send a gift →
              </Button>
            </div>

            {/* About — editorial prose */}
            <div className="mt-8">
              <p className="text-eyebrow text-foreground mb-3">About</p>
              {!enriched && !nudgeDismissed ? (
                <button
                  onClick={() => setAboutOpen(true)}
                  className="relative w-full card-premium border-l-[3px] border-l-accent p-4 text-left"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismissNudge();
                      onDeleted();
                    }}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                  >
                    <span className="text-sm">×</span>
                  </button>
                  <p className="pr-6 text-body text-foreground">
                    Add more to get better messages 💛
                  </p>
                </button>
              ) : !enriched && nudgeDismissed ? (
                <button
                  onClick={() => setAboutOpen(true)}
                  className="flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Add more →
                </button>
              ) : (
                <button
                  onClick={() => setAboutOpen(true)}
                  className="w-full card-premium p-5 text-left"
                >
                  {c.personality && (
                    <p className="font-serif text-lg italic leading-relaxed text-foreground">
                      "{c.personality}"
                    </p>
                  )}
                  {c.loves && c.loves.length > 0 && (
                    <p className="mt-3 text-muted-sm">
                      <span className="text-eyebrow text-accent mr-2">Loves</span>
                      {c.loves.join(" · ")}
                    </p>
                  )}
                  {c.dislikes && c.dislikes.length > 0 && (
                    <p className="mt-2 text-muted-sm">
                      <span className="text-eyebrow text-accent mr-2">Dislikes</span>
                      {c.dislikes.join(" · ")}
                    </p>
                  )}
                  {c.notes && (
                    <p className="mt-3 text-body text-foreground/80 italic">
                      "{c.notes}"
                    </p>
                  )}
                </button>
              )}
            </div>

            {/* Important dates — pills */}
            {allDates.length > 0 && (
              <div className="mt-8">
                <p className="text-eyebrow text-foreground mb-3">Important dates</p>
                <div className="flex flex-wrap gap-2">
                  {allDates.map((d) => {
                    const meta = OCCASION_TYPES[d.occasionType];
                    const dateLabel = format(parseISO(d.date), "d MMM");
                    const labelText = d.label ?? meta.label;
                    return (
                      <button
                        key={d.key}
                        onClick={() => handleWriteMessage(d.occasionType, d.label ?? undefined, d.key !== "birthday" ? d.key : undefined)}
                        className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-4 py-2 text-sm text-foreground transition-colors hover:bg-accent/20"
                      >
                        <span aria-hidden>{meta.emoji}</span>
                        <span>
                          {labelText} <span className="text-muted-foreground">· {dateLabel}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Secondary actions */}
            <div className="mt-10 space-y-2 border-t border-border/50 pt-6">
              <button
                onClick={handleEdit}
                className="flex w-full items-center justify-center gap-2 rounded-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isLocked && <Lock className="h-3.5 w-3.5" />}
                <Pencil className="h-3.5 w-3.5" />
                Edit details &amp; About
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex w-full items-center justify-center gap-2 rounded-full py-2 text-sm text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deleting ? "Removing…" : "Remove from Circle"}
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AboutScreen
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        contact={displayContact}
        onSaved={handleAboutSaved}
      />

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title="This contact is locked"
        benefit="Upgrade to Circle Plus to unlock all your contacts, edit details, and write messages."
      />

      {displayContact && (
        <GiftBrowser
          open={giftBrowserOpen}
          onOpenChange={setGiftBrowserOpen}
          contactId={displayContact.id}
          contactName={displayContact.full_name}
          occasionType={heroEntry?.occasionType}
        />
      )}
    </>
  );
};

function getDaysUntilOccasionSafe(dateStr: string, isRecurring: boolean): number | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = parseISO(dateStr);

  if (!isRecurring) {
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    const diff = differenceInDays(target, today);
    return diff < 0 ? null : diff;
  }

  let next = setYear(d, today.getFullYear());
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(d, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

export default ContactDetail;
